import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, Users, Target, Calendar, Filter, ChevronDown, ChevronUp, Mail, Phone, Save, RotateCcw } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { getBrokerMetrics } from '../services/backendApiService';
import apiClient from '../services/apiService';
import { useData } from '../contexts/DataContext';
import { useActivityLogger } from '../hooks/useActivityLogger';

// All US states
const ALL_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function BrokerMetrics() {
  const { userData, userRole } = useUser();
  const { logBrokerMetrics } = useActivityLogger();
  const [brokerData, setBrokerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [savedDefaults, setSavedDefaults] = useState(null);
  const [filters, setFilters] = useState({
    pickupDateBegin: '',
    pickupDateEnd: '',
    equipmentGroups: ['Flatbed', 'Dry Van', 'Reefer'],
    loadTypes: ['Full', 'Partial'],
    originStates: ALL_STATES,
    destinationStates: ALL_STATES,
    minPayment: 0,
    minRatePerMile: 0.0,
    maxLength: 75,
    maxWeight: 100000
  });

  // Load broker metrics data
  const loadBrokerMetrics = async (currentFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      
      // Convert equipment groups to individual equipment types
      const equipmentMap = {
        'Flatbed': ['F', 'FA', 'FSD', 'FSDV'],
        'Dry Van': ['V'],
        'Reefer': ['R'],
        'Hopper': ['HS'],
        'Conestoga': ['CONG'],
        'Double Drop': ['DD'],
        'Other': ['LA', 'TA', 'SD', 'MAC', 'MAXI', 'SPRTR', 'RGEAR', 'MVAN', 'FCAR', 'FINT', 'FLOGR', 'FMAXI', 'FPULL', 'AUTO', 'BEAM', 'B-TRN', 'CRANE', 'CTANE', 'DBTRL', 'DUMP', 'EXTEN', 'HOT', 'LANDL', 'LTBOY', 'LTMAC', 'LTMEX', 'MHANG', 'OBED', 'PERMT', 'PNEU', 'PUMP', 'RAKE', 'REMOVL', 'SIDEK', 'STEER', 'TANK', 'TAUT', 'TRIDEM', 'LTBOY', 'STEPDCK']
      };
      
      const equipmentTypes = currentFilters.equipmentGroups.flatMap(group => 
        equipmentMap[group] || [group]
      );
      
      const apiFilters = {
        pickup_date_begin: currentFilters.pickupDateBegin || undefined,
        pickup_date_end: currentFilters.pickupDateEnd || undefined,
        equipment_types: equipmentTypes.length > 0 ? equipmentTypes : undefined,
        load_types: currentFilters.loadTypes.length > 0 ? currentFilters.loadTypes : undefined,
        origin_states: currentFilters.originStates.length < ALL_STATES.length ? currentFilters.originStates : undefined,
        destination_states: currentFilters.destinationStates.length < ALL_STATES.length ? currentFilters.destinationStates : undefined,
        min_payment: currentFilters.minPayment > 0 ? currentFilters.minPayment : undefined,
        min_rate_per_mile: currentFilters.minRatePerMile > 0 ? currentFilters.minRatePerMile : undefined,
        max_length: currentFilters.maxLength < 75 ? currentFilters.maxLength : undefined,
        max_weight: currentFilters.maxWeight < 100000 ? currentFilters.maxWeight : undefined,
        limit: 10000
      };
      
      console.log('Loading broker metrics with filters:', apiFilters);
      const data = await getBrokerMetrics(apiFilters);
      setBrokerData(data);

      // Log broker metrics activity using centralized logger
      try {
        await logBrokerMetrics({
          equipment_group: currentFilters.equipmentGroups,
          load_types: currentFilters.loadTypes,
          origin_states: currentFilters.originStates,
          destination_states: currentFilters.destinationStates,
          min_payment: currentFilters.minPayment,
          min_rpm: currentFilters.minRatePerMile,
          max_length: currentFilters.maxLength,
          max_weight: currentFilters.maxWeight,
          pickup_date_begin: currentFilters.pickupDateBegin,
          pickup_date_end: currentFilters.pickupDateEnd
        });
      } catch (logError) {
        console.warn('Failed to log broker metrics activity:', logError);
      }
      
    } catch (err) {
      console.error('Error loading broker metrics:', err);
      setError('Failed to load broker metrics data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize date filters and load data
  useEffect(() => {
    // Set default date range (last 30 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    
    setFilters(prev => ({
      ...prev,
      pickupDateBegin: startDate.toISOString().split('T')[0],
      pickupDateEnd: endDate.toISOString().split('T')[0]
    }));
    
    // Load initial data
    loadBrokerMetrics();
  }, []);

  // Reload data when filters change
  useEffect(() => {
    if (filters.pickupDateBegin && filters.pickupDateEnd) {
      loadBrokerMetrics(filters);
    }
  }, [filters]);

  // Load saved defaults on component mount
  useEffect(() => {
    const loadDefaults = async () => {
      if (userData?.defaults?.bm) {
        try {
          const defaults = JSON.parse(userData.defaults.bm);
          setSavedDefaults(defaults);
          setFilters(prev => ({
            ...prev,
            equipmentGroups: defaults.equipment_group || prev.equipmentGroups,
            loadTypes: defaults.load_types || prev.loadTypes,
            originStates: defaults.origin_states || prev.originStates,
            destinationStates: defaults.destination_states || prev.destinationStates,
            minPayment: defaults.min_payment || prev.minPayment,
            minRatePerMile: defaults.min_rpm || prev.minRatePerMile,
            maxLength: defaults.max_length || prev.maxLength,
            maxWeight: defaults.max_weight || prev.maxWeight
          }));
        } catch (e) {
          console.error('Error loading defaults:', e);
        }
      }
    };
    loadDefaults();
  }, [userData]);


  // Use the broker data directly from the API response
  const brokerMetrics = brokerData || { brokers: [], averages: {} };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleMultiSelectChange = (key, value, isSelected) => {
    setFilters(prev => ({
      ...prev,
      [key]: isSelected 
        ? [...prev[key], value]
        : prev[key].filter(item => item !== value)
    }));
  };

  const handleSaveDefaults = async () => {
    try {
      const defaults = {
        equipment_group: filters.equipmentGroups,
        load_types: filters.loadTypes,
        origin_states: filters.originStates,
        destination_states: filters.destinationStates,
        min_payment: filters.minPayment,
        min_rpm: filters.minRatePerMile,
        max_length: filters.maxLength,
        max_weight: filters.maxWeight
      };

      const response = await apiClient.put(`/users/${userData.sub}/defaults`, {
        default_bm: JSON.stringify(defaults)
      });

      if (response.data.success) {
        setSavedDefaults(defaults);
        alert('Defaults saved successfully!');
      }
    } catch (error) {
      console.error('Error saving defaults:', error);
      alert('Failed to save defaults. Please try again.');
    }
  };

  const handleRestoreDefaults = () => {
    if (savedDefaults) {
      setFilters(prev => ({
        ...prev,
        equipmentGroups: savedDefaults.equipment_group || prev.equipmentGroups,
        loadTypes: savedDefaults.load_types || prev.loadTypes,
        originStates: savedDefaults.origin_states || prev.originStates,
        destinationStates: savedDefaults.destination_states || prev.destinationStates,
        minPayment: savedDefaults.min_payment || prev.minPayment,
        minRatePerMile: savedDefaults.min_rpm || prev.minRatePerMile,
        maxLength: savedDefaults.max_length || prev.maxLength,
        maxWeight: savedDefaults.max_weight || prev.maxWeight
      }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Broker Metrics</h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading broker data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Broker Metrics</h2>
          <p className="mt-2 text-red-600">Error loading data: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Broker Metrics</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Analyze broker performance and market insights
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-white mb-4"
        >
          <Filter className="w-5 h-5" />
          <span>Filters</span>
          {showFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>

        {showFilters && (
          <div className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pickup Date Begin
                </label>
                <input
                  type="date"
                  value={filters.pickupDateBegin}
                  onChange={(e) => handleFilterChange('pickupDateBegin', e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pickup Date End
                </label>
                <input
                  type="date"
                  value={filters.pickupDateEnd}
                  onChange={(e) => handleFilterChange('pickupDateEnd', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            {/* Equipment Groups */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Equipment Types
              </label>
              <div className="flex flex-wrap gap-2">
                {['Flatbed', 'Dry Van', 'Reefer'].map(group => (
                  <button
                    key={group}
                    onClick={() => handleMultiSelectChange('equipmentGroups', group, !filters.equipmentGroups.includes(group))}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filters.equipmentGroups.includes(group)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {group}
                  </button>
                ))}
              </div>
            </div>

            {/* Load Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Load Types
              </label>
              <div className="flex flex-wrap gap-2">
                {['Full', 'Partial'].map(type => (
                  <button
                    key={type}
                    onClick={() => handleMultiSelectChange('loadTypes', type, !filters.loadTypes.includes(type))}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filters.loadTypes.includes(type)
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Origin States */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Origin States
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, originStates: ALL_STATES }))}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
                >
                  Select All
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, originStates: [] }))}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700"
                >
                  Clear All
                </button>
              </div>
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2">
                {ALL_STATES.map(state => (
                  <button
                    key={state}
                    onClick={() => handleMultiSelectChange('originStates', state, !filters.originStates.includes(state))}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      filters.originStates.includes(state)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {state}
                  </button>
                ))}
              </div>
            </div>

            {/* Destination States */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Destination States
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, destinationStates: ALL_STATES }))}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
                >
                  Select All
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, destinationStates: [] }))}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700"
                >
                  Clear All
                </button>
              </div>
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2">
                {ALL_STATES.map(state => (
                  <button
                    key={state}
                    onClick={() => handleMultiSelectChange('destinationStates', state, !filters.destinationStates.includes(state))}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      filters.destinationStates.includes(state)
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {state}
                  </button>
                ))}
              </div>
            </div>

            {/* Numeric Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Min Payment
                </label>
                <input
                  type="number"
                  value={filters.minPayment}
                  onChange={(e) => handleFilterChange('minPayment', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  min="0"
                  step="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Min Rate/Mile
                </label>
                <input
                  type="number"
                  value={filters.minRatePerMile}
                  onChange={(e) => handleFilterChange('minRatePerMile', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Length
                </label>
                <input
                  type="number"
                  value={filters.maxLength}
                  onChange={(e) => handleFilterChange('maxLength', parseInt(e.target.value) || 75)}
                  className="form-input"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Weight
                </label>
                <input
                  type="number"
                  value={filters.maxWeight}
                  onChange={(e) => handleFilterChange('maxWeight', parseInt(e.target.value) || 100000)}
                  className="form-input"
                  min="0"
                  step="1000"
                />
              </div>
            </div>

            {/* Save/Restore Defaults */}
            <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSaveDefaults}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                disabled={!userData}
              >
                <Save className="w-4 h-4" />
                <span>Save Defaults</span>
              </button>
              <button
                onClick={handleRestoreDefaults}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                disabled={!savedDefaults}
              >
                <RotateCcw className="w-4 h-4" />
                <span>Restore Defaults</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Average Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Brokers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {brokerMetrics.averages.broker_count || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Postings/Broker</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {(brokerMetrics.averages.avg_postings_per_broker || 0).toFixed(1)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Unique Lanes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {(brokerMetrics.averages.avg_unique_lanes_per_broker || 0).toFixed(1)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Postings</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {(brokerMetrics.averages.total_postings || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Average Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-600 dark:text-gray-400">Broker Count</p>
            <p className="font-semibold text-gray-900 dark:text-white">{brokerMetrics.averages.broker_count || 0}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Total Postings</p>
            <p className="font-semibold text-gray-900 dark:text-white">{(brokerMetrics.averages.total_postings || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Total Unique Lanes</p>
            <p className="font-semibold text-gray-900 dark:text-white">{(brokerMetrics.averages.total_unique_lanes || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Total Duplicates</p>
            <p className="font-semibold text-gray-900 dark:text-white">{(brokerMetrics.averages.total_duplicates || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Avg Dupe %</p>
            <p className="font-semibold text-gray-900 dark:text-white">{((brokerMetrics.averages.avg_duplicate_percentage || 0) * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Avg Postings/Broker</p>
            <p className="font-semibold text-gray-900 dark:text-white">{(brokerMetrics.averages.avg_postings_per_broker || 0).toFixed(1)}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Avg Unique Lanes/Broker</p>
            <p className="font-semibold text-gray-900 dark:text-white">{(brokerMetrics.averages.avg_unique_lanes_per_broker || 0).toFixed(1)}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Avg Duplicates/Broker</p>
            <p className="font-semibold text-gray-900 dark:text-white">{(brokerMetrics.averages.avg_duplicates_per_broker || 0).toFixed(1)}</p>
          </div>
        </div>
      </div>

      {/* Broker Listing */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Broker Listing</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {brokerMetrics.brokers.length} brokers • Sorted by posting count
          </p>
          {userRole === 'Basic' && (
            <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 rounded">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Note: Contact information is hidden for Basic users. Upgrade to see full details.
              </p>
            </div>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Broker
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Postings
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  %
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cum %
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Unique
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Dupes
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Dupe %
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {brokerMetrics.brokers.slice(0, 50).map((broker, index) => {
                // Censor contact info for Basic users
                const isBasicUser = userRole === 'Basic';
                const displayEmail = isBasicUser && broker.broker_email
                  ? broker.broker_email.replace(/^[^@]+/, '***')
                  : broker.broker_email;
                const displayPhone = isBasicUser && broker.contact_phone
                  ? broker.contact_phone.replace(/(\d{3})-\d{3}-\d{4}/, '$1-***-****')
                  : broker.contact_phone;
                const displayContact = isBasicUser ? 'Contact Hidden' : broker.point_of_contact;

                return (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {broker.broker_name}
                        </div>
                        {broker.broker_city && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {broker.broker_city}, {broker.broker_state}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {displayEmail && (
                          <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                            <Mail className="w-3 h-3 mr-1" />
                            {displayEmail}
                          </div>
                        )}
                        {displayContact && (
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {displayContact}
                          </div>
                        )}
                        {displayPhone && (
                          <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                            <Phone className="w-3 h-3 mr-1" />
                            {displayPhone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {(broker.postings_count || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {((broker.postings_percent || 0) * 100).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {((broker.cumulative_percent || 0) * 100).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {(broker.unique_lanes_count || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {(broker.duplicates_count || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {((broker.duplicate_percentage || 0) * 100).toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {brokerMetrics.brokers.length > 50 && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-400 text-center">
            Showing top 50 brokers • {brokerMetrics.brokers.length} total brokers
          </div>
        )}
      </div>
    </div>
  );
}

export default BrokerMetrics;